import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { useSocket } from "../../contexts/SocketContext";
import {
  LoadingOverlay,
  Text,
  Button,
  Pagination,
  SegmentedControl,
  Modal,
} from "@mantine/core";
import { getExpensesPage } from "../../services/expenses";
import "./Expenses.scss";
import ExpenseItem from "./ExpenseItem";
import { createButtonStyles } from "../../services/globalColor.js";
import ExpenseManager from "./ExpenseManager.jsx";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import { useSetPageTitle } from "../../hooks/useSetPageTitle";
import { usePermissions } from "../../contexts/PermissionsContext";

const Expenses = () => {
  useSetPageTitle("Gastos");
  const { hasPermission } = usePermissions();
  const userData = useSelector((state) => state.user.user);
  const { socket } = useSocket();

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [datePreset, setDatePreset] = useState("all");
  const [customRange, setCustomRange] = useState([null, null]);
  const [specificDate, setSpecificDate] = useState(null);
  const [rangeModalOpen, setRangeModalOpen] = useState(false);
  const [specificModalOpen, setSpecificModalOpen] = useState(false);
  const [tempRange, setTempRange] = useState([null, null]);
  const [tempSpecific, setTempSpecific] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [expenseDataForEdit, setExpenseDataForEdit] = useState(null);

  const getExpensesMethod = async ({ pageOverride } = {}) => {
    try {
      setLoading(true);
      setMessage("Obteniendo gastos ...");
      if (userData?._id) {
        const targetPage = Number.isFinite(Number(pageOverride))
          ? Number(pageOverride)
          : currentPage;
        // Date filters temporarily disabled (Todo mode only)
        // let createdAtFilter = undefined;
        // ... build createdAtFilter here ...
        // const filter = createdAtFilter ? { $or: [ { date: createdAtFilter }, { createdAt: createdAtFilter }, { updatedAt: createdAtFilter } ] } : undefined;
        const filter = undefined;

        const page = await getExpensesPage({
          page: targetPage,
          filter,
        });
        setExpenses(Array.isArray(page?.items) ? page.items : []);
        setTotalPages(
          Number.isFinite(Number(page?.totalPages))
            ? Number(page.totalPages)
            : 1
        );
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
      setExpenses([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData?._id) {
      getExpensesMethod();
    }
  }, [userData]);

  // Date filters temporarily disabled
  // useEffect(() => {
  //   setCurrentPage(1);
  // }, [datePreset, customRange, specificDate]);

  // Fetch when page or filters change
  useEffect(() => {
    if (userData?._id) {
      getExpensesMethod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, datePreset, customRange, specificDate, userData?._id]);

  useEffect(() => {
    if (!socket) return;
    const onAppUpsert = (payload) => {
      const isValid = payload && payload.collection === "expenses";
      if (!isValid) return;
      getExpensesMethod({ pageOverride: currentPage });
    };
    socket.on("app:upsert", onAppUpsert);
    return () => socket.off("app:upsert", onAppUpsert);
  }, [socket, currentPage, userData?._id]);

  const closeExpenseManagerModalHanlder = () => {
    setIsOpen(false);
    setExpenseDataForEdit(null);
  };

  const saveExpenseManagerModalHandler = async () => {
    await getExpensesMethod({ pageOverride: currentPage });
    setExpenseDataForEdit(null);
  };

  if (!userData?._id) {
    return (
      <div className="expenses container">
        <Text color="dimmed">No se encontró información del usuario</Text>
      </div>
    );
  }

  return (
    <>
      <LoadingOverlay
        visible={loading}
        overlayProps={{ blur: 2 }}
      />
      <ExpenseManager
        getExpensesMethod={saveExpenseManagerModalHandler}
        userId={userData._id}
        isOpen={isOpen}
        onClose={closeExpenseManagerModalHanlder}
        expenseData={expenseDataForEdit}
      />

      <div className="expenses">
        <h1 className="hide-on-mobile">Gastos</h1>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
          {hasPermission({ feature: "gastos", action: "Crear" }) && (
            <Button
              type="button"
              onClick={() => setIsOpen(true)}
              style={{ minWidth: 120, fontWeight: 700 }}
            >
              Añadir
            </Button>
          )}
        </div>

        {/** Date filters temporarily disabled (Todo mode only). Modals kept here for future use. */}
        {false && (
          <>
            <Modal opened={false} onClose={() => {}} title="Seleccionar rango">
              <DatePickerInput type="range" value={null} onChange={() => {}} valueFormat="YYYY-MM-DD" clearable dropdownOpened={false} />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
                <Button variant="default" onClick={() => {}}>Cancelar</Button>
                <Button  onClick={() => {}}>Aplicar</Button>
              </div>
            </Modal>
            <Modal opened={false} onClose={() => {}} title="Seleccionar fecha">
              <DatePickerInput value={null} onChange={() => {}} valueFormat="YYYY-MM-DD" clearable dropdownOpened={false} />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
                <Button variant="default" onClick={() => {}}>Cancelar</Button>
                <Button  onClick={() => {}}>Aplicar</Button>
              </div>
            </Modal>
          </>
        )}

        <div
          className="table-container"
          style={{ textAlign: "left" }}
        >
          {expenses.length === 0 ? (
            <Text
              color="dimmed"
              align="center"
            >
              No hay gastos registrados
            </Text>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th >Fecha</th>
                  <th >Título</th>
                  <th >Concepto</th>
                  <th >Monto</th>
                  <th ></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <ExpenseItem
                    key={expense?._id}
                    expense={expense}
                    getExpensesMethod={() =>
                      getExpensesMethod({ pageOverride: currentPage })
                    }
                  />
                ))}
              </tbody>
            </table>
          )}
          {expenses.length > 0 && (
            <div
              style={{
                marginTop: 20,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                total={totalPages}
                value={currentPage}
                onChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Expenses;
